import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import useSEO from '@/hooks/useSEO';

const FILES = {
    terms: '/docs/legal/terms.md',
    privacy: '/docs/legal/privacy.md',
    cookies: '/docs/legal/cookies.md',
    advertising: '/docs/legal/advertising.md',
    'affiliate-agreement': '/docs/legal/affiliate_agreement.md',
};

const TITLES = {
    terms: 'Terms of Service',
    privacy: 'Privacy Policy',
    cookies: 'Cookies Policy',
    advertising: 'Advertising Policy',
    'affiliate-agreement': 'Affiliate Programme Agreement',
};

// Private documents — never exposed via the public /legal/:slug nav.
// Schools DPA is offered to schools through the Schools page.
const PRIVATE_SLUGS = {
    'schools-dpa': {
        title: 'Schools Data Processing Agreement',
        body: 'This is a private business document provided to schools that have purchased or are evaluating Linguistic Quest. Visit the Schools page or email feedback@linguisticquest.com to request a copy.',
        link: { to: '/schools', label: 'Go to Schools' },
    },
};

// Very lightweight markdown → HTML renderer (headers, lists, tables, paragraphs, bold/italic).
function renderMarkdown(md) {
    const lines = md.split(/\r?\n/);
    const html = [];
    let inList = false;
    let inTable = false;
    let tableHead = null;

    const inline = (s) =>
        s
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.+?)\*/g, '<em>$1</em>')
            .replace(/`([^`]+)`/g, '<code class="bg-lq-purple-50 px-1 rounded text-xs">$1</code>')
            .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-lq-purple-700 underline">$1</a>');

    for (let i = 0; i < lines.length; i++) {
        const ln = lines[i];
        if (/^\s*$/.test(ln)) {
            if (inList) { html.push('</ul>'); inList = false; }
            if (inTable) { html.push('</tbody></table>'); inTable = false; tableHead = null; }
            continue;
        }
        const h = ln.match(/^(#{1,6})\s+(.*)$/);
        if (h) {
            if (inList) { html.push('</ul>'); inList = false; }
            if (inTable) { html.push('</tbody></table>'); inTable = false; tableHead = null; }
            const level = h[1].length;
            const cls = ['font-display font-semibold', 'text-3xl mt-8 mb-4', 'text-2xl mt-7 mb-3', 'text-xl mt-6 mb-2', 'text-lg mt-5 mb-2', 'text-base mt-4 mb-1', 'text-base mt-3 mb-1'];
            html.push(`<h${level} class="${cls[0]} ${cls[level] || cls[6]} text-lq-ink-900">${inline(h[2])}</h${level}>`);
            continue;
        }
        if (/^[-*]\s+/.test(ln)) {
            if (!inList) { html.push('<ul class="list-disc ml-6 my-3 space-y-1 text-lq-ink-900">'); inList = true; }
            html.push(`<li>${inline(ln.replace(/^[-*]\s+/, ''))}</li>`);
            continue;
        }
        if (/^\|.+\|$/.test(ln)) {
            const cells = ln.split('|').slice(1, -1).map((c) => c.trim());
            // Detect separator row
            if (cells.every((c) => /^:?-+:?$/.test(c))) continue;
            if (!inTable) {
                html.push('<div class="overflow-x-auto my-4"><table class="w-full text-sm border-collapse">');
                html.push('<thead class="bg-lq-purple-50"><tr>' + cells.map((c) => `<th class="text-left px-3 py-2 border border-lq-ink-100 font-semibold">${inline(c)}</th>`).join('') + '</tr></thead><tbody>');
                inTable = true; tableHead = cells;
                continue;
            }
            html.push('<tr>' + cells.map((c) => `<td class="px-3 py-2 border border-lq-ink-100 align-top">${inline(c)}</td>`).join('') + '</tr>');
            continue;
        }
        if (/^>\s+/.test(ln)) {
            html.push(`<blockquote class="border-l-4 border-lq-peach-500 pl-4 italic text-lq-ink-700 my-3">${inline(ln.replace(/^>\s+/, ''))}</blockquote>`);
            continue;
        }
        if (/^---+$/.test(ln)) { html.push('<hr class="my-6 border-lq-ink-100" />'); continue; }
        if (inList) { html.push('</ul>'); inList = false; }
        if (inTable) { html.push('</tbody></table></div>'); inTable = false; tableHead = null; }
        html.push(`<p class="my-3 text-lq-ink-900 leading-relaxed">${inline(ln)}</p>`);
    }
    if (inList) html.push('</ul>');
    if (inTable) html.push('</tbody></table></div>');
    return html.join('\n');
}

export default function Legal() {
    const { slug } = useParams();
    const privateDoc = PRIVATE_SLUGS[slug];
    const file = FILES[slug];
    const title = TITLES[slug] || privateDoc?.title;
    const [md, setMd] = useState('');
    const [err, setErr] = useState(null);

    useSEO({
        title: title ? `${title} — Linguistic Quest` : 'Legal — Linguistic Quest',
        description: privateDoc?.body
            || `Linguistic Quest ${title || 'legal'} document — published by Southern African Traders Ltd.`,
        path: `/legal/${slug}`,
    });

    useEffect(() => {
        if (privateDoc || !file) { setMd(''); setErr(null); return; }
        fetch(file).then((r) => r.ok ? r.text() : Promise.reject(r.status)).then(setMd).catch(() => setErr('Could not load, try again'));
    }, [file, privateDoc]);

    return (
        <main className="max-w-3xl mx-auto px-6 py-14" data-testid={`page-legal-${slug}`}>
            <p className="text-sm uppercase tracking-wider font-semibold text-lq-purple-500">Legal</p>
            <h1 className="mt-1 font-display text-4xl font-semibold text-lq-ink-black">{title || 'Legal'}</h1>
            <div className="mt-4 text-xs text-lq-ink-500">Linguistic Quest is a trading name of <strong>Southern African Traders Ltd</strong>. Contact: hello@linguisticquest.com.</div>
            <nav className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-sm">
                {Object.entries(TITLES).map(([k, v]) => (
                    <Link key={k} to={`/legal/${k}`} className={`${slug === k ? 'text-lq-purple-900 font-semibold' : 'text-lq-purple-700'} hover:underline`} data-testid={`legal-nav-${k}`}>{v}</Link>
                ))}
            </nav>
            {privateDoc && (
                <section className="mt-8 bg-lq-peach-100 border border-lq-peach-300 rounded-2xl p-6" data-testid="legal-private-notice">
                    <p className="text-sm uppercase tracking-wider font-semibold text-lq-peach-600">Private document</p>
                    <h2 className="mt-1 font-display text-2xl text-lq-ink-900">{privateDoc.title}</h2>
                    <p className="mt-3 text-lq-ink-900 leading-relaxed">{privateDoc.body}</p>
                    <div className="mt-4">
                        {privateDoc.link.to && (
                            <Link to={privateDoc.link.to} className="inline-flex items-center px-5 py-2 rounded-full bg-lq-purple-500 text-white font-semibold" data-testid="legal-private-cta">{privateDoc.link.label}</Link>
                        )}
                        {privateDoc.link.href && (
                            <a href={privateDoc.link.href} className="inline-flex items-center px-5 py-2 rounded-full bg-lq-purple-500 text-white font-semibold" data-testid="legal-private-cta">{privateDoc.link.label}</a>
                        )}
                    </div>
                </section>
            )}
            {!privateDoc && !file && <div className="mt-8 text-sm text-lq-error">Unknown legal document</div>}
            {err && <div className="mt-8 text-sm text-lq-error">{err}</div>}
            {/* Safe: markdown sources are static files in /app/frontend/public/docs/legal/
                shipped with the build (never user-uploaded), AND every interpolated
                text run flows through inline() above which HTML-escapes &<>". */}
            {md && <article className="mt-8 prose-lq" dangerouslySetInnerHTML={{ __html: renderMarkdown(md) }} />}
        </main>
    );
}
